import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const MEELINK_PROMO_STORAGE_KEY = 'metag_meelink_lifetime_promo_dismissed';
export const MEELINK_URL = 'https://meelink.cc';

function readDismissed() {
  try {
    return localStorage.getItem(MEELINK_PROMO_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismissed() {
  try {
    localStorage.setItem(MEELINK_PROMO_STORAGE_KEY, '1');
  } catch {
    // Private mode / blocked storage — still hide for this session.
  }
}

/**
 * Advertisement-only MeeLink Pro lifetime offer.
 * Redemption lives on meelink.cc; this never collects email or calls verify-customer.
 *
 * @param {'banner' | 'section'} variant
 */
export default function MeelinkLifetimePromo({ variant = 'banner' }) {
  const [dismissed, setDismissed] = useState(readDismissed);

  const dismiss = () => {
    persistDismissed();
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {!dismissed && (
        variant === 'section' ? (
          <SectionCallout key="meelink-promo-section" onDismiss={dismiss} />
        ) : (
          <BannerCallout key="meelink-promo-banner" onDismiss={dismiss} />
        )
      )}
    </AnimatePresence>
  );
}

function BannerCallout({ onDismiss }) {
  return (
    <motion.div
      role="region"
      aria-label="MeeLink Pro offer for MeTag customers"
      data-testid="meelink-lifetime-promo"
      data-variant="banner"
      className="relative mb-6 overflow-hidden rounded-2xl border border-violet-500/25 bg-gradient-to-r from-violet-500/10 via-fuchsia-500/5 to-transparent px-4 py-3 sm:px-5"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex items-start gap-3 sm:items-center">
        <div className="hidden sm:flex w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm sm:text-base font-medium leading-snug">
            Got a MeTag card?{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-300 to-fuchsia-300">
              MeeLink Pro is free for life.
            </span>
          </p>
          <p className="text-slate-400 text-xs sm:text-sm mt-0.5">
            Redeem on MeeLink with the email you used to buy your card.
          </p>
        </div>
        <a
          href={MEELINK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-300 hover:text-white transition-colors"
          data-testid="meelink-lifetime-promo-link"
        >
          meelink.cc
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <button
          type="button"
          onClick={onDismiss}
          className="flex-shrink-0 p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Dismiss MeeLink offer"
          data-testid="meelink-lifetime-promo-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

function SectionCallout({ onDismiss }) {
  return (
    <motion.section
      role="region"
      aria-label="MeeLink Pro offer for MeTag customers"
      className="py-24"
      data-testid="meelink-lifetime-promo"
      data-variant="section"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      exit={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10" />
          <button
            type="button"
            onClick={onDismiss}
            className="absolute top-4 right-4 z-10 p-2 rounded-xl text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Dismiss MeeLink offer"
            data-testid="meelink-lifetime-promo-dismiss"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-emerald-400 font-mono text-sm uppercase tracking-widest">
                For MeTag owners
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-4">
                Got a MeTag card? MeeLink Pro is free for life.
              </h2>
              <p className="text-slate-400 text-lg mb-6">
                One tap from your card, one page for everything they need. Paid MeTag
                customers can claim lifetime Pro on MeeLink — redeem on their site with
                the email from your order.
              </p>
              <Button
                asChild
                className="btn-primary"
              >
                <a
                  href={MEELINK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                  data-testid="meelink-lifetime-promo-link"
                >
                  Claim it on meelink.cc
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </div>

            <div className="flex justify-center">
              <div className="w-64 h-64 rounded-3xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex flex-col items-center justify-center text-center px-6">
                <Sparkles className="w-8 h-8 text-white/80 mb-3" />
                <span className="text-4xl font-black text-white">MeeLink</span>
                <span className="text-white/70 text-sm mt-2 tracking-wide uppercase">Pro · lifetime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
